import { auth } from "../../../backend/firebaseFunctions";

/**
 * Send email using the cloud function
 * @param {Array} recipients - Array of recipient objects with id, name, email
 * @param {string} title - Email subject/title
 * @param {string} htmlBody - Email body content (HTML)
 * @returns {Promise<Object>} - Response object with success status and message
 */
export const sendEmail = async (recipients, title, htmlBody) => {
    try {
        // Check if user is authenticated
        if (!auth.currentUser) {
            throw new Error("No authenticated user found");
        }

        // Validate required fields
        if (!recipients || !Array.isArray(recipients) || recipients.length === 0) {
            throw new Error("Recipients array is required and cannot be empty");
        }

        if (!title || title.trim() === "") {
            throw new Error("Email title is required");
        }

        if (!htmlBody || htmlBody.trim() === "") {
            throw new Error("Email body is required");
        }

        // Extract email addresses from recipient objects
        const recipientEmails = recipients.map(recipient => {
            if (!recipient.email) {
                throw new Error(`Missing email for recipient: ${recipient.name || recipient.id}`);
            }
            return recipient.email;
        });

        // Prepare the data for the cloud function
        const cloudFunctionData = {
            recipients: recipientEmails,
            subject: title.trim(),
            html: htmlBody.trim(),
        };

        // Call the cloud function using fetch
        const response = await fetch("https://europe-west3-jks-israel-app.cloudfunctions.net/sendEmail", {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
            },
            body: JSON.stringify(cloudFunctionData)
        });

        const responseData = await response.json();

        if (responseData.success) {
            return {
                success: true,
                message: responseData.message || "Email sent successfully",
                recipientCount: responseData.recipientCount,
                timestamp: responseData.timestamp
            };
        } else {
            throw new Error(responseData.error || "Failed to send email");
        }

    } catch (error) {
        console.error("Error sending email:", error);

        // Handle specific error types
        if (error.message.includes("auth")) {
            throw new Error("Authentication error - Please sign in again");
        } else if (error.message.includes("invalid")) {
            throw new Error(error.message || "Invalid arguments provided");
        } else if (error.message.includes("permission")) {
            throw new Error("Permission denied - You don't have permission to send emails");
        } else {
            throw new Error(error.message || "Failed to send email");
        }
    }
};

export default {
    sendEmail
};
